import { List, ListItemButton, ListItemIcon, ListItemText, Paper, Stack, Typography } from "@mui/material";
import Timeoff from "../../models/Timeoff";
import { useMemo, useState } from "react";
import TimeoffViewDialog from "../TimeOffViewDialog";
import OpsStringUtils from "../../utils/OpsStringUtils";
import { TimeOffType } from "../../types/TimeOffType";
import OpsDateUtils from "../../utils/OpsDateUtils";
import OpsPanel from "../OpsPanel";
import icon from "../../img/icons/work_logs.svg";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { Token } from "../../theme";
import { TimeOffStatus } from "../../utils/enums/TimeOffStatus";

interface Props {
  /**
   * Data to be rendered
   */
  rows: Timeoff[];
  /**
   * Holiday data to be optionally shown
   */
  holidays: Timeoff[];
}

export default function UpcomingTimeoffsTile({ rows, holidays }: Props) {
  const [viewTimeoff, setViewTimeoff] = useState<Timeoff | undefined>();

  const closeViewDialog = () => {
    setViewTimeoff(undefined);
  };

  const combinedRows = useMemo(() => {
    //reordered so holidays goes to the bottom (less important than timeoffs)
    return [...rows, ...holidays];
  }, [rows, holidays]);

  const isNonClickable = (row: Timeoff) => {
    return row.type === TimeOffType.BIRTHDAY || row.type === TimeOffType.HOLIDAY;
  };

  const getSecondaryText = (row: Timeoff): string => {
    if (row.type === TimeOffType.HOLIDAY) return "Holiday";
    if (row.type === TimeOffType.BIRTHDAY) return "Birthday";
    if (row.approvedBy) {
      return `Approved by ${row.approvedBy.firstName} ${row.approvedBy.lastName}`;
    }
    return OpsStringUtils.capitalize(row.status);
  };

  const getPrimaryText = (row: Timeoff): string => {
    if (row.type === TimeOffType.HOLIDAY) return row.requesterProjectName ?? "";
    const name = `${row.requester.firstName} ${row.requester.lastName}`;
    if (row.type === TimeOffType.BIRTHDAY) return name;
    return row.totalDays ? `${name} — ${row.totalDays} days` : name;
  };

  const hasContent = rows.length > 0 || holidays.length > 0;

  const getIcon = (row: Timeoff) => {
    if (row.type === TimeOffType.BIRTHDAY) return <CakeOutlinedIcon fontSize="small" />;
    if (row.type === TimeOffType.VACATION && row.status === TimeOffStatus.APPROVED) {
      return <BeachAccessOutlinedIcon fontSize="small" sx={{ color: Token.timeoffFg }} />;
    }
    return <EventOutlinedIcon fontSize="small" sx={{ color: Token.timeoffFg }} />;
  };

  return hasContent ? (
    <Stack>
      <OpsPanel title="Upcoming Timeoffs" actionsRequired={false} icon={icon}>
        <Paper elevation={0} style={{ overflow: "auto", maxHeight: "15.5em" }}>
          <List disablePadding>
            {combinedRows.map((row, index) => (
              <ListItemButton
                dense
                key={index}
                sx={{
                  borderBottom: `1px solid ${Token.borderSubtle}`,
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: Token.textTertiary }}>{getIcon(row)}</ListItemIcon>
                <ListItemText
                  primary={getPrimaryText(row)}
                  secondary={getSecondaryText(row)}
                  onClick={isNonClickable(row) ? () => null : () => setViewTimeoff(row)}
                  primaryTypographyProps={{ fontWeight: "bold" }}
                />
                <Typography fontSize="11.5px" color={Token.textTertiary} sx={{ flexShrink: 0, pl: 1 }}>
                  {OpsDateUtils.formatInUTCAsDDDMMM(new Date(row.shortStartDate))}
                </Typography>
              </ListItemButton>
            ))}
          </List>
        </Paper>
      </OpsPanel>

      {viewTimeoff && <TimeoffViewDialog viewTimeoff={viewTimeoff} selectedFiles={viewTimeoff!.documentation} onClose={closeViewDialog} />}
    </Stack>
  ) : null;
}
